import { persistAuthTokens } from '../../../utils/authUtils';

const OFFICE_FAMLA_TOKEN_KEY = 'famla.officeAuth.famlaToken';

export type FamlaSessionExchange = {
    accessToken: string;
    refreshToken?: string;
};

export function readPersistedFamlaToken(): string | null {
    try {
        return localStorage.getItem(OFFICE_FAMLA_TOKEN_KEY);
    } catch {
        return null;
    }
}

export function persistFamlaToken(token: string, refreshToken?: string): void {
    try {
        localStorage.setItem(OFFICE_FAMLA_TOKEN_KEY, token);
    } catch {
        // ignore quota / private mode
    }
    persistAuthTokens(token, refreshToken);
}

export function clearPersistedFamlaToken(): void {
    try {
        localStorage.removeItem(OFFICE_FAMLA_TOKEN_KEY);
    } catch {
        // ignore
    }
}

/**
 * Exchange an Entra access token (Office SSO / dialog) for a Famla JWT session.
 */
export async function exchangeEntraForFamlaSession(
    entraAccessToken: string
): Promise<FamlaSessionExchange | null> {
    const apiUrl =
        process.env.REACT_APP_API_BASE_URL || 'http://localhost:9540/api/v1';

    const response = await fetch(`${apiUrl}/auth/microsoft-bearer`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${entraAccessToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        if (process.env.REACT_APP_DEBUG === 'true') {
            // eslint-disable-next-line no-console
            console.warn(
                '[OfficeAuth] Famla session exchange failed:',
                response.status,
                await response.text().catch(() => '')
            );
        }
        return null;
    }

    const data = await response.json();
    const sessionData = data?.data;
    if (!sessionData?.access_token) {
        return null;
    }

    const result: FamlaSessionExchange = {
        accessToken: sessionData.access_token,
        refreshToken: sessionData.refresh_token,
    };
    persistFamlaToken(result.accessToken, result.refreshToken);
    return result;
}
